import {
  Injectable,
  type Signal,
  type WritableSignal,
  computed,
  effect,
  inject,
  linkedSignal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { distinctUntilChanged, map, pairwise, startWith } from 'rxjs';

/**
 * Coalesces every `as: 'query'` param write into ONE `router.navigate` per
 * microtask. Each `paramStore` owns a single key and used to navigate on its
 * own; when two stores changed in the same tick (e.g. a branch card click
 * commits `branchId` and then advances the stepper, which projects `step`),
 * their two `router.navigate({queryParamsHandling: 'merge'})` calls raced: the
 * router serialises imperative navigations, so the later one superseded the
 * in-flight earlier one, and because each `merge` is built from the *stale*
 * pre-navigation URL the cancelled navigation's param was dropped from the URL
 * (the in-memory signal kept its `.set()` value, so only the URL diverged).
 *
 * Buffering all writes in the tick and flushing them as a single merged
 * navigation removes the race: both params ride in one navigation, so neither
 * clobbers the other.
 */
@Injectable({ providedIn: 'root' })
class QueryParamWriter {
  readonly #router = inject(Router);
  #pending: Record<string, unknown> | null = null;
  #replaceUrl = true;
  #route: ActivatedRoute | null = null;

  write(
    key: string,
    value: unknown,
    replaceUrl: boolean,
    route: ActivatedRoute,
  ): void {
    if (this.#pending === null) {
      this.#pending = {};
      this.#replaceUrl = true;
      this.#route = route;
      queueMicrotask(() => this.#flush());
    }
    this.#pending[key] = value;
    // Any writer that wants a history entry (push) wins over a replace, so a
    // batched push is never silently downgraded to a replace.
    this.#replaceUrl &&= replaceUrl;
  }

  #flush(): void {
    const queryParams = this.#pending;
    const replaceUrl = this.#replaceUrl;
    const route = this.#route;
    this.#pending = null;
    this.#route = null;
    if (!queryParams) {
      return;
    }
    this.#router.navigate([], {
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl,
      relativeTo: route ?? undefined,
    });
  }
}

export type ParamStoreOptions<T> = {
  possibleValues?: readonly T[];
  initialValue?: T;
  multiple?: boolean;
  replaceUrl?: boolean;
  parse?: (value: string) => T;
  as?: 'path' | 'query';
};

type ParamStoreResult<T, M extends boolean> = {
  current: WritableSignal<M extends true ? T[] : T | null>;
  previous: Signal<M extends true ? T[] : T | null>;
};

export interface ParamStore {
  // parse overloads
  <T>(
    key: string | number,
    options: { parse: (value: string) => T } & ParamStoreOptions<T> & {
        multiple: true;
      },
  ): ParamStoreResult<T, true>;
  <T>(
    key: string | number,
    options: { parse: (value: string) => T } & ParamStoreOptions<T>,
  ): ParamStoreResult<T, false>;
  // possibleValues overloads
  <T>(
    key: string | number,
    options: ParamStoreOptions<T> & {
      possibleValues: readonly T[];
      multiple: true;
    },
  ): ParamStoreResult<T, true>;
  <T>(
    key: string | number,
    options: ParamStoreOptions<T> & { possibleValues: readonly T[] },
  ): ParamStoreResult<T, false>;
  // initialValue overloads
  <T>(
    key: string | number,
    options: ParamStoreOptions<T> & { initialValue: T; multiple: true },
  ): ParamStoreResult<T, true>;
  <T>(
    key: string | number,
    options: ParamStoreOptions<T> & { initialValue: T },
  ): ParamStoreResult<T, false>;
  // multiple: true alone (no inference hint) -> infer T from default
  <T extends string = string>(
    key: string | number,
    options: ParamStoreOptions<T> & { multiple: true },
  ): ParamStoreResult<T, true>;
  // default overload (multiple: false or undefined)
  <T extends string = string>(
    key: string | number,
    options?: ParamStoreOptions<T>,
  ): ParamStoreResult<T, false>;
}

export const paramStore: ParamStore = <T extends string>(
  key: string | number,
  {
    possibleValues = [],
    as = 'query',
    initialValue,
    multiple = false,
    replaceUrl = true,
    parse = (v) => v as T,
  }: ParamStoreOptions<T> = {},
) => {
  const router = inject(Router);
  const route = inject(ActivatedRoute);
  const queryWriter = inject(QueryParamWriter);

  const obs = as === 'path' ? route.paramMap : route.queryParamMap;
  const value = toSignal(
    obs.pipe(
      map((params, index) => {
        if (multiple) {
          const values = params.getAll(`${key}`);
          if (index === 0 && values.length === 0) {
            const defaultValue = initialValue ?? possibleValues[0];
            if (defaultValue !== undefined) {
              values.push(defaultValue);
            }
          }
          return values.map(parse);
        }
        const v = params.get(`${key}`) || null;
        if (index === 0 && v === null) {
          return initialValue ?? possibleValues[0] ?? null;
        }
        return v ? parse(v) : null;
      }),
      startWith(multiple ? [] : null),
      distinctUntilChanged((a, b) => {
        if (multiple) {
          return (a as T[])?.length === (b as T[])?.length;
        }
        return a === b;
      }),
      pairwise(),
      takeUntilDestroyed(),
    ),
  );

  const current = linkedSignal(
    () => value()?.[1] ?? (multiple ? [] : undefined),
  );
  const previous = computed(() => value()?.[0] ?? (multiple ? [] : undefined));

  effect(() => {
    const currentValue = current();
    if (as === 'query' && currentValue !== undefined) {
      // Buffered into a single per-tick navigation so a same-tick write to a
      // different query key can't clobber this one (see QueryParamWriter).
      queryWriter.write(`${key}`, currentValue, replaceUrl, route);
    } else if (as === 'path' && !multiple) {
      const prev = previous();
      const curr = current();
      if (prev != null && curr != null) {
        const currentUrl = router.url.replace(String(prev), String(curr));
        router.navigate([currentUrl]);
      }
    }
  });

  return {
    current,
    previous,
  };
};
